import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ContentCard } from '../types';
import { queryKeys } from '../services/queryClient';
import { getErrorMessage, isAPIError } from '../services/api';
import apiClient from '../services/api';

interface PopularTitlesResponse {
  titles: ContentCard[];
  nextToken?: string;
  count: number;
  filters: {
    type?: string;
    limit: number;
  };
}

interface UsePopularContentOptions {
  limit?: number;
  type?: 'movie' | 'tvSeries';
  enabled?: boolean;
  staleTime?: number;
  cacheTime?: number;
  retry?: boolean | number | ((failureCount: number, error: any) => boolean);
}

interface UsePopularContentResult {
  popularMovies: ContentCard[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  isRefetching: boolean;
  isFetching: boolean;
}

/**
 * Hook for fetching top popular content with configurable parameters
 * Supports caching, error handling, and refetch functionality
 */
export const usePopularContent = (options: UsePopularContentOptions = {}): UsePopularContentResult => {
  const {
    limit = 20,
    type = 'movie',
    enabled = true,
    staleTime = 5 * 60 * 1000, // 5 minutes default
    cacheTime = 10 * 60 * 1000, // 10 minutes default
    retry,
  } = options;

  // Validate limit parameter
  const validatedLimit = Math.min(Math.max(limit, 1), 100);

  const {
    data,
    isLoading,
    error,
    refetch,
    isRefetching,
    isFetching,
  } = useQuery<PopularTitlesResponse, Error>({
    queryKey: queryKeys.content.popular(validatedLimit, type),
    queryFn: async (): Promise<PopularTitlesResponse> => {
      const params = new URLSearchParams({
        limit: validatedLimit.toString(),
        ...(type && { type }),
      });

      const response = await apiClient.get<PopularTitlesResponse>(
        `/api/titles/top-popular?${params}`
      );

      // Validate response structure
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Invalid response format from popular titles API');
      }

      const { titles = [], nextToken, count = 0, filters } = response.data;

      // Ensure titles is an array
      if (!Array.isArray(titles)) {
        throw new Error('Expected titles to be an array');
      }

      return {
        titles,
        nextToken,
        count,
        filters: filters || { limit: validatedLimit, type },
      };
    },
    enabled,
    staleTime,
    cacheTime,
    retry: retry !== undefined ? retry : (failureCount: number, error: any) => {
      // Don't retry on authentication errors
      if (isAPIError(error) && error.response?.status === 401) {
        return false;
      }
      // Don't retry on client errors (4xx) except rate limiting
      if (isAPIError(error) && error.response?.status) {
        const status = error.response.status;
        if (status >= 400 && status < 500 && status !== 429) {
          return false;
        }
      }
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    onError: (error) => {
      console.error('[usePopularContent] Query failed:', {
        error: getErrorMessage(error),
        limit: validatedLimit,
        type,
      });
    },
  });

  return {
    popularMovies: data?.titles || [],
    isLoading,
    error,
    refetch,
    isRefetching,
    isFetching,
  };
};

/**
 * Specialized hook for fetching top 100 popular movies
 * Optimized for the PopularMoviesCarousel component
 */
export const useTop100PopularMovies = (): UsePopularContentResult => {
  return usePopularContent({
    limit: 100,
    type: 'movie',
    staleTime: 10 * 60 * 1000, // Cache longer for top 100 (10 minutes)
    cacheTime: 30 * 60 * 1000, // Keep in cache longer (30 minutes)
  });
};

/**
 * Hook for fetching popular TV shows
 */
export const usePopularTVShows = (limit = 20): UsePopularContentResult => {
  return usePopularContent({
    limit,
    type: 'tvSeries',
  });
};

/**
 * Enhanced hook for fetching top 100 popular titles for homepage filtering
 * Returns both the titles and a Set of IDs for efficient filtering
 * Note: Backend has a maximum limit of 100 items per request
 */
interface UseTop500PopularContentResult {
  popularTitles: ContentCard[];
  popularTitleIds: Set<string>;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  isRefetching: boolean;
  isFetching: boolean;
}

export const useTop500PopularContent = (): UseTop500PopularContentResult => {
  const {
    data,
    isLoading,
    error,
    refetch,
    isRefetching,
    isFetching,
  } = useQuery<PopularTitlesResponse, Error>({
    queryKey: queryKeys.content.popular(100, undefined), // Backend max is 100, not 500
    queryFn: async (): Promise<PopularTitlesResponse> => {
      const params = new URLSearchParams({
        limit: '100', // Backend maximum is 100
      });

      const response = await apiClient.get<PopularTitlesResponse>(
        `/api/titles/top-popular?${params}`
      );

      // Validate response structure
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Invalid response format from popular titles API');
      }

      const { titles = [], nextToken, count = 0, filters } = response.data;

      // Ensure titles is an array
      if (!Array.isArray(titles)) {
        throw new Error('Expected titles to be an array');
      }

      return {
        titles,
        nextToken,
        count,
        filters: filters || { limit: 100 },
      };
    },
    enabled: true,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes (longer for top 100)
    cacheTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    retry: (failureCount: number, error: any) => {
      // Don't retry on authentication errors
      if (isAPIError(error) && error.response?.status === 401) {
        return false;
      }
      // Don't retry on client errors (4xx) except rate limiting
      if (isAPIError(error) && error.response?.status) {
        const status = error.response.status;
        if (status >= 400 && status < 500 && status !== 429) {
          return false;
        }
      }
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    onError: (error) => {
      console.error('[useTop500PopularContent] Query failed:', {
        error: getErrorMessage(error),
        limit: 100,
      });
    },
  });

  // Create a Set of popular title IDs for efficient filtering
  const popularTitleIds = React.useMemo(() => {
    if (!data?.titles) return new Set<string>();
    return new Set(data.titles.map(title => title.id));
  }, [data?.titles]);

  return {
    popularTitles: data?.titles || [],
    popularTitleIds,
    isLoading,
    error,
    refetch,
    isRefetching,
    isFetching,
  };
};

export default usePopularContent;